import React from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useCallback, useState } from "react";
import {
    Toaster,
    Toast,
    ToastTitle,
    useToastController,
    useId,
} from "@fluentui/react-components";
import { companyForm } from "../data/data";
import { buildInitialState, FormState } from "../utils/formUtils";
import NewCompanyContactForm from "../components/NewCompanyContactForm";
import { apiUrl } from "../utils/api";
import { buildCompanyContactCreatePayload } from "../utils/buildCompanyContactCreatePayload";
import { rechtsformAusParam, rechtsformKeyAusParam } from "../utils/kontaktAnlageTypen";
import { loadLegalFormOptionsCached } from "../utils/unternehmensBegriff";
import { firmaVorbelegungAusParams } from "../utils/nameAusSuche";
import { nachAnlageZuweisen, ruecksprungAusParams } from "../utils/kundenakteRuecksprung";
import type { EmployeeRolesContact } from "../components/relationships/RelatedEmployeesRoles";
import { mitZusatzPayload, type ZusatzPayload } from "../utils/refZuVorbelegung";

/** Callbacks, mit denen die Anlage-Page in einem Fenster (statt Vollseite) läuft. */
export type ContactPageEmbedded = {
    /** `felder` = gespeicherter Formularstand (Name, Rechtsform …) für den Aufrufer. */
    onSuccess?: (contactId: number | null, felder?: FormState) => void;
    onClose?: () => void;
    onDirtyChange?: (dirty: boolean) => void;
    onTitleSuffix?: (suffix: string | null) => void;
    /** Vorbelegte Formularfelder (Feldname → Wert), z. B. eine E-Mail aus einem
     *  Termin-Teilnehmer (Termine-SP8/A17). Zählt nicht als „dirty". */
    initialValues?: Record<string, string>;
    /**
     * Beschriftung des Speichern-Knopfs. Aus einer Schnellanlage geöffnet heißt
     * er „Übernehmen" bzw. „Weiter" (Mail zuweisen) — er speichert UND trägt
     * den Kontakt dort ein, wo die Schnellanlage stand (User 02.10.2026).
     */
    speichernText?: string;
    /**
     * Bezug zum Elternkontakt, der mit dem Anlage-POST mitgeht
     * (`verantwortungVonKontaktId`, `imHaushaltVon` — siehe POST /contact).
     */
    zusatzPayload?: ZusatzPayload;
};

// Auto-vorbelegte Felder (Ownership-Preselect + Herkunft-Default) zählen NICHT
// als „dirty" — sonst wäre jedes frisch geöffnete Fenster sofort schmutzig.
const AUTO_POPULATED_KEYS = new Set([
    "foUserId", "boType", "boTeamId", "boUserId",
    "contactOriginSelections", "contactOriginOther",
    "register", // Default „HRB" wird beim Öffnen gesetzt
    // Geschäftsjahr 01.01. (Kalenderjahr) und Land „Deutschland" werden beim
    // Öffnen gesetzt — ein frisch geöffnetes Fenster ist deshalb nicht dirty.
    "geschaeftsjahrTag", "geschaeftsjahrMonat", "country",
]);

type NewCompanyContactPageProps = {
    embedded?: ContactPageEmbedded;
    /** Aus dem Einstieg „Neuer Kontakt" (Status & Rollen) — zählt nicht als dirty. */
    vorbelegung?: Record<string, string>;
    /** Wechsel auf die Privatanlage; bekommt den aktuellen Formularstand. */
    onTypWechsel?: (felder: FormState) => void;
};

const NewCompanyContactPage: React.FC<NewCompanyContactPageProps> = ({ embedded, vorbelegung, onTypWechsel } = {}) => {
    const navigate = useNavigate();
    const toasterId = useId("new-company-contact-toaster");
    const { dispatchToast } = useToastController(toasterId);
    const notify = (intent: "success" | "error", message: string) =>
        dispatchToast(
            <Toast>
                <ToastTitle>{message}</ToastTitle>
            </Toast>,
            { intent },
        );
    const [savedContactId, setSavedContactId] = useState<number | null>(null);
    // Query-Parameter gelten nur auf der Vollseite — im Fenster-Modus gehört die
    // URL der Seite HINTER dem Fenster (siehe `rechtsform` weiter unten).
    const [searchParams] = useSearchParams();
    // „Kontakt nicht gefunden – neu anlegen" (globale Suche/Kontaktliste):
    // `?name=` wird zum Firmennamen. Nur beim ersten Rendern gelesen.
    const [urlVorbelegung] = useState<Record<string, string>>(() =>
        embedded ? {} : firmaVorbelegungAusParams(searchParams),
    );
    const [ruecksprung] = useState(() => (embedded ? { zurueck: null, ordnerId: null } : ruecksprungAusParams(searchParams)));
    // Vorbelegung (SP8/A17, Suchbegriff) fließt auch in initialRef → zählt nicht als dirty.
    const [formFields, setFormFields] = useState<FormState>(() => ({
        ...buildInitialState(companyForm),
        ...(embedded?.initialValues ?? {}),
        ...urlVorbelegung,
        ...(vorbelegung ?? {}),
    }));
    const initialRef = React.useRef<FormState>({
        ...buildInitialState(companyForm),
        ...(embedded?.initialValues ?? {}),
        ...urlVorbelegung,
        ...(vorbelegung ?? {}),
    });

    const updateField = useCallback((name: string, value: string) => {
        setFormFields((prev) => ({ ...prev, [name]: value }));
    }, []);

    // Rechtsform-Vorbelegung aus dem Anlage-Menü („Neu → Verein"). Der Schlüssel
    // steht in der URL und wird über den Katalog aufgelöst — die Id
    // unterscheidet sich zwischen DEV und PROD. Frei änderbar bleibt die
    // Rechtsform in der Maske.
    //
    // Im Fenster-Modus (embedded) gibt es keinen eigenen Query-String: Die URL
    // gehört dort der Seite HINTER dem Fenster, deren `?rechtsform=` nichts mit
    // diesem Entwurf zu tun hat.
    const rechtsformKey = embedded ? null : rechtsformKeyAusParam(searchParams.get("rechtsform"));
    // Schon beim ersten Rendern bekannt — die Maske muss die Autoerkennung aus
    // dem Firmennamen sperren, BEVOR der Katalog da ist (sonst überschreibt sie
    // die Vorbelegung, siehe NewCompanyContactForm). Der Schlüssel ist ohne
    // Katalog prüfbar, ein vertippter Param sperrt also nichts.
    // Auch eine Rechtsform aus der Schnellanlage (`initialValues.legalFormId`)
    // sperrt die Autoerkennung — sonst überschriebe der Firmenname sie.
    const rechtsformVorbelegt = !!rechtsformKey || !!embedded?.initialValues?.legalFormId;
    React.useEffect(() => {
        if (!rechtsformKey) return;
        let aktiv = true;
        loadLegalFormOptionsCached()
            .then((options) => {
                const eintrag = rechtsformAusParam(rechtsformKey, options);
                if (!aktiv || !eintrag) return;
                // Wie bei `initialValues`: die Vorbelegung wandert MIT in den
                // Ausgangsstand, sonst schlägt der Close-Guard sofort an
                // („ungespeicherte Änderungen"), obwohl niemand etwas getippt
                // hat. Über `initialRef` statt über AUTO_POPULATED_KEYS, damit
                // eine später von Hand geänderte Rechtsform weiterhin zählt.
                initialRef.current.gesellschaftsform = eintrag.legalForm;
                initialRef.current.legalFormId = String(eintrag.id);
                updateField("gesellschaftsform", eintrag.legalForm);
                updateField("legalFormId", String(eintrag.id));
            })
            .catch(() => {
                /* ohne Katalog bleibt die Rechtsform leer — die Maske fragt sie ab */
            });
        return () => {
            aktiv = false;
        };
    }, [rechtsformKey, updateField]);

    // Dirty-Tracking + Live-Titel für den eingebetteten Fenster-Modus.
    React.useEffect(() => {
        if (embedded?.onDirtyChange) {
            const dirty = Object.keys(formFields).some(
                (k) => !AUTO_POPULATED_KEYS.has(k)
                    && (formFields[k] ?? "") !== (initialRef.current[k] ?? ""),
            );
            embedded.onDirtyChange(dirty);
        }
        if (embedded?.onTitleSuffix) {
            embedded.onTitleSuffix(String(formFields.name ?? "").trim() || null);
        }
    }, [formFields, embedded]);

    const handleClose = () => {
        if (embedded?.onClose) { embedded.onClose(); return; }
        navigate("/contacts");
    };

    const toTrimmedString = (value: unknown): string =>
        typeof value === "string" ? value.trim() : "";

    const firstNonEmpty = (...values: unknown[]): string => {
        for (const value of values) {
            const normalized = toTrimmedString(value);
            if (normalized) return normalized;
        }
        return "";
    };

    const toBool = (value: unknown): boolean => {
        if (typeof value === "boolean") return value;
        const normalized = toTrimmedString(value).toLowerCase();
        return normalized === "yes" || normalized === "ja" || normalized === "true";
    };

    type AddressPayload = {
        addressDescription: string;
        street: string;
        postalCode: string;
        city: string;
        ortsteil: string;
        country: string;
        type: string;
        isDefault: boolean;
    };

    const parseAdditionalAddresses = (): AddressPayload[] => {
        const raw = formFields.additionalAddresses;
        if (typeof raw !== "string" || !raw.trim()) return [];

        try {
            const parsed = JSON.parse(raw);
            if (!Array.isArray(parsed)) return [];

            const normalized: AddressPayload[] = [];

            parsed.forEach((addr) => {
                    if (!addr || typeof addr !== "object") return null;
                    const item = addr as Record<string, unknown>;

                    const addressDescription = toTrimmedString(item.addressDescription);
                    const street = toTrimmedString(item.street);
                    const postalCode = firstNonEmpty(item.postalCode, item.postalcode, item.plz);
                    const city = firstNonEmpty(item.city, item.ort);
                    const ortsteil = firstNonEmpty(item.ortsteil, item.cityArea);
                    const country = firstNonEmpty(item.country, item.land);

                    const hasAddressData = [street, postalCode, city, ortsteil, country, addressDescription].some(Boolean);
                    if (!hasAddressData) return;

                    normalized.push({
                        addressDescription,
                        street,
                        postalCode,
                        city,
                        ortsteil,
                        country,
                        type: "private",
                        isDefault: toBool(item.isDefault),
                    });
                });

            return normalized;
        } catch {
            return [];
        }
    };

    const asNumberId = (value: unknown): number | null => {
        if (typeof value === "number" && Number.isFinite(value)) return value;
        if (typeof value === "string" && value.trim()) {
            const parsed = Number(value);
            return Number.isFinite(parsed) ? parsed : null;
        }
        return null;
    };

    const extractContactId = (data: unknown): number | null => {
        if (!data || typeof data !== "object") return null;
        const obj = data as Record<string, unknown>;
        return asNumberId(obj.id) ?? asNumberId((obj.contact as Record<string, unknown> | undefined)?.id);
    };

    const extractSeatAddressId = (data: unknown): number | null => {
        if (!data || typeof data !== "object") return null;
        const obj = data as Record<string, unknown>;
        const addresses = obj.addresses;
        if (!Array.isArray(addresses)) return null;

        const seat = addresses.find((entry) => {
            if (!entry || typeof entry !== "object") return false;
            const item = entry as Record<string, unknown>;
            const desc = toTrimmedString(item.addressDescription).toLowerCase();
            const isDefault = Boolean(item.isDefault);
            return desc === "unternehmenssitz" || isDefault;
        });

        if (!seat || typeof seat !== "object") return null;
        return asNumberId((seat as Record<string, unknown>).id);
    };

    const extractDefaultPhoneId = (data: unknown): number | null => {
        if (!data || typeof data !== "object") return null;
        const obj = data as Record<string, unknown>;

        const defaultPhone = obj.defaultPhone;
        if (defaultPhone && typeof defaultPhone === "object") {
            const id = asNumberId((defaultPhone as Record<string, unknown>).id);
            if (id) return id;
        }

        const communications = obj.communications;
        if (!Array.isArray(communications)) return null;

        const defaultComm = communications.find((entry) => {
            if (!entry || typeof entry !== "object") return false;
            const item = entry as Record<string, unknown>;
            const type = toTrimmedString(item.type).toLowerCase();
            const label = toTrimmedString(item.label).toLowerCase();
            const isDefault = Boolean(item.isDefault);
            return type === "phone" && (isDefault || label === "zentrale");
        });

        if (!defaultComm || typeof defaultComm !== "object") return null;
        return asNumberId((defaultComm as Record<string, unknown>).id);
    };

    const handleSave = async (): Promise<number | false> => {
        const built = buildCompanyContactCreatePayload(formFields, {
            foUserId: (formFields.foUserId as string) || undefined,
            boType: (formFields.boType as "NONE" | "USER" | "TEAM") || undefined,
            boTeamId: (formFields.boTeamId as string) || undefined,
            boUserId: (formFields.boUserId as string) || undefined,
            inhouseContactUserId: (formFields.inhouseContactUserId as string) || undefined,
        });
        if (!built) return false;
        const {
            basePayload,
            payloadWithDetails,
            addresses,
            companyCommunications,
            bankAccounts,
            hasCompanyCommunications,
        } = built;

        // Keine zweite Pflichtprüfung mehr: Pflicht sind ausschließlich
        // Firmenname und Rechtsform (PO 17.08.2026, wie `companyRequiredChecks`).
        // Hier hing bis 08/2026 eine eigene Rufnummern-Sperre — sie war seit der
        // PO-Notiz vom 02.08. nicht mehr synchron mit der Regel und blockte das
        // Speichern, obwohl der Speichern-Knopf freigegeben hatte.

        // RBAC-KRITISCH: Die Folge-POSTs unten gehen an POST /contact (create),
        // das die Ownership bei JEDEM Aufruf neu ableitet. Ohne foUserId/boType
        // im Body setzt der Controller für nicht-gfgl-Caller accessLevel='TENANT'
        // und überschreibt damit den frisch korrekt gesetzten PRIVATE/TEAM-Wert
        // (= mandantenweites Leak). Daher trägt JEDER Folge-Body die Ownership
        // mit, so dass create() denselben PRIVATE/TEAM-Wert wie der Erst-Create
        // ableitet. (Quelle: basePayload, von #176 befüllt.)
        const ownershipFields: Record<string, unknown> = {
            ...(basePayload.foUserId !== undefined ? { foUserId: basePayload.foUserId } : {}),
            ...(basePayload.boType !== undefined ? { boType: basePayload.boType } : {}),
            ...(basePayload.boTeamId !== undefined ? { boTeamId: basePayload.boTeamId } : {}),
            ...(basePayload.boUserId !== undefined ? { boUserId: basePayload.boUserId } : {}),
        };

        const postContact = async (body: Record<string, unknown>): Promise<unknown> => {
            const res = await fetch(apiUrl("contact"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });

            const responseText = await res.text();
            if (!res.ok) {
                throw new Error(responseText || `HTTP ${res.status}`);
            }

            if (!responseText) return null;

            try {
                return JSON.parse(responseText);
            } catch {
                return responseText;
            }
        };

        const fetchContactById = async (contactId: number): Promise<unknown> => {
            const res = await fetch(apiUrl(`contact/${contactId}`));
            if (!res.ok) return null;

            const responseText = await res.text();
            if (!responseText) return null;

            try {
                return JSON.parse(responseText);
            } catch {
                return null;
            }
        };

        const updateAddressesForExistingContact = async (contactId: number): Promise<number | null> => {
            if (!addresses.length) return null;

            const updateCandidates: Record<string, unknown>[] = [
                { id: contactId, ...ownershipFields, addresses },
                { ...basePayload, id: contactId, addresses },
            ];

            for (const candidate of updateCandidates) {
                try {
                    const updated = await postContact(candidate);
                    const seatAddressId = extractSeatAddressId(updated);
                    if (seatAddressId) return seatAddressId;
                } catch {
                    // try next candidate
                }
            }

            return extractSeatAddressId(await fetchContactById(contactId));
        };

        const updateCompanyCommunicationsForExistingContact = async (contactId: number): Promise<number | null> => {
            if (!hasCompanyCommunications) return null;

            const updateCandidates: Record<string, unknown>[] = [
                { id: contactId, ...ownershipFields, communications: companyCommunications },
                { ...basePayload, id: contactId, communications: companyCommunications },
            ];

            for (const candidate of updateCandidates) {
                try {
                    const updated = await postContact(candidate);
                    const phoneId = extractDefaultPhoneId(updated);
                    if (phoneId) return phoneId;
                } catch {
                    // try next candidate
                }
            }

            return extractDefaultPhoneId(await fetchContactById(contactId));
        };

        // Banken im Staged-Fallback nachreichen: basePayload trägt KEINE
        // bankAccounts (die stecken nur in payloadWithDetails). Ohne diesen
        // Pfad gingen im Fehlerfall ALLE Bankverbindungen verloren. Pro Konto
        // ein POST /contact/:id/bank (Update-Recht des Erstellers genügt).
        const persistBanksForExistingContact = async (contactId: number): Promise<void> => {
            if (!bankAccounts.length) return;
            for (const bank of bankAccounts) {
                try {
                    const res = await fetch(apiUrl(`contact/${contactId}/bank`), {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(bank),
                    });
                    if (!res.ok) {
                        console.warn("Persisting company bank account failed", {
                            contactId,
                            status: res.status,
                        });
                    }
                } catch (err) {
                    // Eine einzelne Bank darf das Speichern der Firma nicht abbrechen.
                    console.warn("Persisting company bank account threw", { contactId, err });
                }
            }
        };

        // ── „Menschen im/fürs Unternehmen" — Personen + Beziehungen persistieren ──
        // Die im RelatedEmployeesRoles-Block erfassten Personen werden als
        // Contacts angelegt (oder per id referenziert) und je Eintrag eine
        // ContactRelation Firma→Person gespeichert. Ohne diesen Pfad ging der
        // gesamte Block beim Speichern verloren.
        const ownershipForSubPerson = (): Record<string, unknown> => {
            // Sub-Person erbt die RBAC-Verantwortlichkeit der Firma (foUserId/BO),
            // damit sie nicht TENANT-weit sichtbar wird. Die Felder sind beim
            // Mount vom Form vorbelegt (siehe Ownership-Preselect).
            const foUserId = toTrimmedString(formFields.foUserId);
            const boType = toTrimmedString(formFields.boType);
            const boTeamId = toTrimmedString(formFields.boTeamId);
            return {
                ...(foUserId ? { foUserId } : {}),
                ...(boType ? { boType } : {}),
                ...(boType === "TEAM" && boTeamId ? { boTeamId } : {}),
            };
        };

        const buildPersonPayloadFromEmployee = (
            emp: EmployeeRolesContact,
        ): Record<string, unknown> => {
            const lastname = toTrimmedString(emp.lastname);
            const firstName = toTrimmedString(emp.firstname);
            const comms: Array<{ type: string; label: string; value: string; isDefault: boolean }> = [];
            let phoneDefaultSet = false;
            const pushPhone = (type: "phone" | "mobile", value?: string) => {
                const v = toTrimmedString(value);
                if (!v) return;
                const isDefault = !phoneDefaultSet;
                phoneDefaultSet = true;
                comms.push({ type, label: "Geschäftlich", value: v, isDefault });
            };
            pushPhone("phone", emp.phone);
            pushPhone("mobile", emp.mobile);
            const email = toTrimmedString(emp.email);
            if (email) comms.push({ type: "email", label: "Geschäftlich", value: email, isDefault: true });

            const street = toTrimmedString(emp.privateStreet);
            const postalCode = toTrimmedString(emp.privatePostalCode);
            const city = toTrimmedString(emp.privateCity);
            const hasAddress = [street, postalCode, city].some(Boolean);
            const addresses = hasAddress
                ? [{
                    addressDescription: "Wohnanschrift",
                    street, postalCode, city,
                    ortsteil: "", country: "",
                    type: "private", isDefault: true,
                }]
                : [];

            return {
                lastname,
                displayName: firstName ? `${lastname}, ${firstName}` : lastname,
                ...(firstName ? { firstName } : {}),
                ...(toTrimmedString(emp.birthDate) ? { birthDate: toTrimmedString(emp.birthDate) } : {}),
                ...(addresses.length ? { addresses } : {}),
                ...(comms.length ? { communications: comms } : {}),
                // Firmen-Mitarbeiter/Ansprechpartner tragen „Verantwortung fürs
                // Unternehmen" (bis 29.09.2026 „Mensch fürs Unternehmen").
                statusName: "Verantwortung fürs Unternehmen",
                ...ownershipForSubPerson(),
            };
        };

        const extractEmployeeContacts = (): EmployeeRolesContact[] => {
            const raw = formFields.employeeContacts;
            if (typeof raw !== "string" || !raw.trim()) return [];
            try {
                const parsed = JSON.parse(raw);
                return Array.isArray(parsed) ? (parsed as EmployeeRolesContact[]) : [];
            } catch {
                return [];
            }
        };

        const persistEmployeeRelations = async (companyId: number): Promise<void> => {
            const employees = extractEmployeeContacts();
            for (const emp of employees) {
                try {
                    let personId = asNumberId(emp.id);
                    if (!personId) {
                        const lastname = toTrimmedString(emp.lastname);
                        if (!lastname) continue; // ohne Nachname keinen Contact anlegen
                        const created = await postContact(buildPersonPayloadFromEmployee(emp));
                        personId = extractContactId(created);
                        if (!personId) continue; // Person-Anlage fehlgeschlagen → Relation überspringen
                    }
                    const relationType = toTrimmedString(emp.role) || "Mitarbeiter";
                    // Externe Firma (Freitext) hat KEINE Contact-id → nicht als
                    // externalCompanyId speicherbar. Damit der Name nicht verloren
                    // geht (Backend würde ihn als reines isExtern-Bool verwerfen),
                    // in die note schreiben. note = varchar(500).
                    const company = toTrimmedString(emp.company);
                    const relationNote = toTrimmedString(emp.relationnote);
                    const note = company
                        ? (relationNote ? `Externe Firma: ${company} — ${relationNote}` : `Externe Firma: ${company}`)
                        : (relationNote || undefined);
                    await fetch(apiUrl("contact-relation"), {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            contactSrcId: companyId,
                            contactDestId: personId,
                            relationType,
                            function: toTrimmedString(emp.function) || undefined,
                            share: emp.sharePercent != null ? String(emp.sharePercent) : undefined,
                            note,
                            ...(company ? { isExtern: true } : {}),
                        }),
                    });
                } catch (err) {
                    // Einzelne Person darf weder die Schleife noch die (bereits
                    // gespeicherte) Firma abbrechen.
                    console.warn("Persisting employee relation failed", { emp, err });
                }
            }
        };

        try {
            const createdOrUpdated = await postContact(mitZusatzPayload(payloadWithDetails, embedded?.zusatzPayload));
            const contactId = extractContactId(createdOrUpdated);

            if (contactId) {
                setSavedContactId(contactId);
                // Hier stand bis 09/2026 ein ZWEITER POST /contact, der die
                // Standard-Anschrift und die Zentralrufnummer nachtragen
                // sollte. Er war seit `DropContactDefaultFksBackfillIsDefault`
                // (05/2026) wirkungslos — `defaultAddress`/`defaultPhone` sind
                // seither berechnete Projektionen ohne Spalte, maßgeblich ist
                // `isDefault` an der Kindzeile, und das setzt der Payload oben
                // längst selbst. Wirkung hatte der Nachtrag nur als Schaden:
                // POST /contact ist der ANLAGE-Pfad, und der setzte ohne Status
                // im Body „Interessent" über die eben gewählte „Kunde" (Meldung
                // 11.09.2026) — dazu verbrauchte jeder Aufruf eine frische
                // Kontaktnummer.

                await persistEmployeeRelations(contactId);
            }

            return contactId ?? false;
        } catch (error) {
            console.warn("Primary company payload failed, retrying with staged fallback", error);
            try {
                const created = await postContact(mitZusatzPayload(basePayload, embedded?.zusatzPayload));
                const createdContactId = extractContactId(created);
                if (!createdContactId) {
                    return false;
                }
                setSavedContactId(createdContactId);

                // Anders als der Nachtrag oben tragen diese beiden Aufrufe
                // echte Daten nach (Anschrift bzw. Kommunikationswege einer
                // Firma, die der Erst-Payload nicht unterbringen konnte). Die
                // Markierung als Standard steckt in `isDefault` an der Zeile
                // selbst — es braucht dafür keinen weiteren Aufruf.
                const seatAddressId = await updateAddressesForExistingContact(createdContactId);
                if (!seatAddressId) {
                    console.warn("Company contact created but seat address update failed", {
                        contactId: createdContactId,
                    });
                }

                if (hasCompanyCommunications) {
                    await updateCompanyCommunicationsForExistingContact(createdContactId);
                }

                await persistBanksForExistingContact(createdContactId);

                await persistEmployeeRelations(createdContactId);

                return createdContactId;
            } catch (fallbackError: unknown) {
                console.error("Saving company contact failed", fallbackError);
                return false;
            }
        }
    };

    const handleSaveSuccess = (contactId: number) => {
        if (embedded?.onSuccess) { embedded.onSuccess(contactId, formFields); return; }
        // Aus dem Zuweisen-Popup der Alten Kundenakten („weitere Infos", B2):
        // Ordner zuweisen und dorthin zurück, wo der Nutzer angefangen hat.
        if (!embedded && ruecksprung.ordnerId) {
            void nachAnlageZuweisen(ruecksprung.ordnerId, contactId).then(() =>
                navigate(ruecksprung.zurueck ?? `/contacts/${contactId}`),
            );
            return;
        }
        navigate(`/contacts/${contactId}`);
    };

    return (
        <>
            <Toaster toasterId={toasterId} />
            <NewCompanyContactForm
                formFields={formFields}
                updateField={updateField}
                rechtsformVorbelegt={rechtsformVorbelegt}
                speichernText={embedded?.speichernText}
                onSave={handleSave}
                onSaveSuccess={handleSaveSuccess}
                onClose={handleClose}
                onTypWechsel={onTypWechsel ? () => onTypWechsel(formFields) : undefined}
            />
        </>
    );
};

export default NewCompanyContactPage;
